/* eslint-disable max-lines */
import { integrationOptionsAtom } from '@/atoms/integrationAtoms';
import { getSpecialtyIdentifyTraits } from '@/components/utils/specialty-identify-traits';
import { workspaceHydrationGatePorts } from '@/composition/hosts/workspace-hydration-gate-ports';
import { CurrentUserProvider } from '@/context/current-user-context';
import { useRtfCopyIdentity } from '@/features/copySettings/useRtfCopyIdentity';
import { useIsProjectsEnabled, useIsSessionPageV2Enabled } from '@/features/flags';
import { useHcpVerificationIntercomStatus } from '@/features/settings/account/components/hcp-verification/hcp-verification-intercom-status';
import {
  accessAllPermissions,
  hasUpdatedAbilityAtom,
  updateAbility,
} from '@/helpers/casl/ability';
import { useOverlaySetEnabled } from '@/hooks/overlay/useOverlaySetEnabled';
import { useQueryUserEHR } from '@/hooks/query/integrations/useQueryUserEHR';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { useQueryDocumentTemplates } from '@/hooks/query/useQueryDocumentTemplates';
import { useQueryJoinedTeams } from '@/hooks/query/useQueryJoinedTeams';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { GET_TEAM_DETAILS, useQueryTeamDetails } from '@/hooks/query/useQueryTeamDetails';
import { useQueryUserForContext } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useCheckTokenOnTabFocus } from '@/hooks/useCheckTokenExpiry';
import { useDatabaseRegion } from '@/hooks/useDatabaseRegion';
import { useInvokeOnce } from '@/hooks/useInvokeOnce';
import { usePublishSourceControlSyncLifecycle } from '@/hooks/useSyncSourceControlSelections';
import { setActiveTeam, trackNativeAuthenticated } from '@/lib/analytics/scribe/auth';
import { selectActiveTeamId } from '@/lib/analytics/workspace-analytics-context';
import { KindeDesktopBridgeMount } from '@/lib/auth/components/KindeDesktopBridge';
import { isLogoutInFlight, useIsLogoutInFlight } from '@/lib/authInFlight';
import { sleep } from '@/lib/utils';
import { countriesToRegion } from '@/lists/countriesToRegion';
import { getSessionPageV2EnrollmentSource } from '@/models/analytics/features/user';
import { getAppType } from '@/models/analyticsScribeModel';
import { AuthProvider, getPersistedAuthProvider } from '@/models/authModel';
import { type Dispatch, type RootState } from '@/store';
import { useDesktopAuth } from '@/tauri/auth0/desktop-auth-context';
import { DesktopAuthProvider } from '@/tauri/auth0/provider';
import { recorderFmc } from '@/tauri/controller/recording';
import { reactKindeConstants } from '@/tauri/kinde';
import { isTauri, tauriShouldUseTranslations } from '@/tauri/utils';
import { type IntegrationRequest } from '@/types';
import { isJWTExpired } from '@/utils/auth/isJWTExpired';
import { setIntercomProperties } from '@/utils/identify';
import { logger } from '@/utils/logger/logger';
import { setDatadogProperties } from '@/utils/tracing';
import { extractIdpSubFromAccessToken } from '@heidi/auth/tokens';
import { WorkspaceHydrationGate } from '@heidi/workspace';
import { useCurrentWorkspace, useWorkspaceId } from '@heidi/workspace/data';
import { KindeProvider, useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { FullPageLoader } from '../ui/full-page-loader';

type AppState = {
  redirectTo?: string;
};

export function TauriAuth({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { locale } = useIntl();
  const [redirecting, setRedirecting] = useState(false);
  const isLoggingOut = useIsLogoutInFlight();

  // useEffect(() => {
  //   if (isTauri()) {
  //     void recorderFmc.stop_recording_without_backup_file();
  //   }
  // }, []);

  const onRedirectCallback = async (
    user: {
      given_name: string | null;
      id: string | null;
      family_name: string | null;
      email: string | null;
      picture: string | null;
    },
    app_state?: AppState
  ) => {
    if (isTauri()) {
      setRedirecting(true);
      if (app_state?.redirectTo) {
        await router.push(app_state.redirectTo);
      }
      await sleep(1000);
      setRedirecting(false);
    }
  };

  useInvokeOnce(() => {
    // clears all app data on first open app
    void recorderFmc.clear_audio_file_directory();
  });

  return (
    <KindeProvider
      clientId={reactKindeConstants.clientId}
      domain={reactKindeConstants.domain}
      logoutUri={window.location.origin}
      redirectUri={
        window.location.origin +
        (tauriShouldUseTranslations() ? `/${locale}/scribe` : '/scribe')
      }
      onRedirectCallback={(user, app_state?: AppState) => {
        logger.debug({ user, app_state });
        logger.debug('on redirect callback');
        if (isTauri()) {
          void onRedirectCallback(user, app_state);
          void recorderFmc.clear_audio_file_directory();
        }
        trackNativeAuthenticated();
      }}
    >
      {/* Mirrors useKinde().getToken into kindeDesktopBridgeAtom so the
          cache's Kinde-desktop branch can pierce the SDK from
          module-level code. Mounted INSIDE <KindeProvider> — the
          underlying useKindeAuth() hook only resolves the live SDK
          context here; mounting it higher up reads the SDK's empty
          default context, which silently breaks the bridge. */}
      <KindeDesktopBridgeMount />
      <DesktopAuthProvider>
        <WorkspaceHydrationGate ports={workspaceHydrationGatePorts}>
          <TauriWrapper>
            {redirecting || isLoggingOut ? <FullPageLoader /> : children}
          </TauriWrapper>
        </WorkspaceHydrationGate>
      </DesktopAuthProvider>
    </KindeProvider>
  );
}

const TauriWrapper = ({ children }: { children: React.ReactNode }) => {
  // Get auth state from both providers
  const {
    getUser,
    isAuthenticated: kindeIsAuthenticated,
    isLoading: kindeIsLoading,
    getToken,
  } = useKindeAuth();
  const { isAuthenticated: auth0IsAuthenticated, isLoading: auth0IsLoading } =
    useDesktopAuth();

  const kindeUser = getUser();
  const dispatch = useDispatch<Dispatch>();
  const storedToken = useSelector((state: RootState) => state.auth.token);
  const authProvider = useSelector((state: RootState) => state.auth.authProvider);

  // Read authProvider directly from localStorage to avoid Redux rehydration timing issues.
  // This is synchronous and runs before any useEffect, ensuring we know the correct
  // auth provider even if Redux hasn't finished rehydrating yet.
  const persistedAuthProvider = getPersistedAuthProvider();
  const isAuth0Desktop =
    authProvider === AuthProvider.AUTH0_DESKTOP ||
    persistedAuthProvider === AuthProvider.AUTH0_DESKTOP;

  // Use auth state from the active provider
  const isAuthenticated = isAuth0Desktop ? auth0IsAuthenticated : kindeIsAuthenticated;
  const isLoading = isAuth0Desktop ? auth0IsLoading : kindeIsLoading;
  const { data: joinedTeamsData, isFetched: isJoinedTeamsFetched } =
    useQueryJoinedTeams();
  //TODO: Fix tracking once Accuro is live
  const { data: userEhr } = useQueryUserEHR();
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  // Active team = the joined team in the current workspace (one team per
  // workspace), falling back to the first joined team when there's no match.
  const activeTeamId = selectActiveTeamId(joinedTeamsData?.joined_teams, workspaceId);
  const { data: teamDetails } = useQueryTeamDetails(activeTeamId ?? '');
  const { data: specialties } = useQuerySpecialties((data) => data.specialties);
  const userQuery = useQueryUserForContext();
  const { data: user } = userQuery;
  const databaseRegion = useDatabaseRegion();
  const hcpVerificationStatus = useHcpVerificationIntercomStatus();
  const isLoggingOut = useIsLogoutInFlight();
  useRtfCopyIdentity(
    isAuthenticated && !isLoading && !isLoggingOut ? (user?.kinde_user_id ?? null) : null
  );

  usePublishSourceControlSyncLifecycle({
    isAuthenticated,
    isAuthenticationLoading: isLoading,
    hasFreshUserData: userQuery.isFetchedAfterMount,
    isUserQueryError: userQuery.isError,
  });

  const { data: billingInfo } = useQueryUserBillingInfo();
  const workspace = useCurrentWorkspace();
  const isSessionPageV2Enabled = useIsSessionPageV2Enabled();
  const isProjectsEnabled = useIsProjectsEnabled();
  const integrationOptions = useAtomValue(integrationOptionsAtom);
  const { refetch: refetchDocumentTemplates } = useQueryDocumentTemplates();
  const cpuArch = useSelector((state: RootState) => state.tauriUpdateModel.cpu_arch);
  useCheckTokenOnTabFocus();

  useEffect(() => {
    if (isTauri()) {
      void dispatch.tauriUpdateModel.getCpuArchOnce();
    }
  }, [dispatch.tauriUpdateModel]);

  // Only fetch Kinde token if not using Auth0 Desktop
  useEffect(() => {
    if (isAuth0Desktop) {
      return;
    }

    // A reload right after a desktop Auth0 login can rehydrate the Redux
    // token before `authProvider` (the throttled `persist:root` write lost
    // the race with the navigation) and before `DesktopAuthProvider` has read
    // the native session — so `isAuth0Desktop` reads false while the desktop
    // bridge is still resolving, or already reports a live session. The Kinde
    // stale-token cleanup below must never run in that window: it wiped the
    // freshly minted Auth0 session and stranded the Windows post-signup boot
    // on a loader (DSK-936). An unauthenticated desktop session is cleaned up
    // by `DesktopAuthProvider.loadAuthSession` itself.
    if (auth0IsLoading || auth0IsAuthenticated) {
      return;
    }

    // Block while SDK is still initialising — getToken() may return
    // intermediate/stale tokens during this window.
    if (isLoading) {
      return;
    }

    const setup = async () => {
      const kindeToken = await getToken?.();
      logger.debug({ kindeToken });
      // Idempotence gate: only sync + warm when either the SDK's
      // token differs from the persisted one OR `authProvider` is
      // not yet KINDE. `useKinde()`'s Tauri branch returns a fresh
      // arrow per render, so this effect re-runs on every render of
      // TauriWrapper; without the anti-churn part every render would
      // `clearAuthCache()` (bumping generation and nulling the
      // session metadata atom mid-boot) and re-warm, causing
      // metadata consumers to flicker null and discarding the
      // previous run's in-flight fetch.
      //
      // The provider-inequality clause is load-bearing for legacy
      // Kinde-desktop installs (persisted token, no persisted
      // `authProvider` — this block is the ONLY code path that
      // writes it, as the comment below notes). Under a
      // token-inequality-only gate a relaunch within the
      // access-token TTL (no rotation, so SDK yields the same token
      // redux-persist already holds) would skip the block on every
      // render → provider stays `undefined` → dispatcher's desktop
      // null-provider transient gate → boot retries exhaust →
      // permanent `'loading'` skeleton with no path to the login
      // UI. Guarding on `authProvider !== KINDE` forces the block
      // to run once, dispatch the provider, and let the next render
      // (provider now KINDE, token equal) skip normally.
      if (
        kindeToken &&
        (kindeToken !== storedToken || authProvider !== AuthProvider.KINDE)
      ) {
        const isLogoutInProgress = isLogoutInFlight();
        if (!isLogoutInProgress) {
          dispatch.auth.setToken(kindeToken);
          // Mark the active provider as Kinde BEFORE warming the cache
          // below. The cache routes through
          // readFromProviderSourceOfTruth, which dispatches on
          // state.auth.authProvider via getEffectiveAuthProvider() (which
          // reads Redux on Tauri). Without this dispatch, the value is
          // still `undefined` (initial state — Kinde-desktop has no
          // earlier code path that writes it; auth0-desktop does the
          // equivalent at src/tauri/auth0/provider.tsx). The switch then
          // falls to default, returns null, and the cache classifies as
          // bare-null, leaving authStateAtom='unauthenticated' despite a
          // valid token. setAuthProviderAsSessionCookie dispatches
          // _setAuthProvider as part of its effect — the cookie write is
          // a no-op in the Tauri runtime but the Redux write is what we
          // need.
          dispatch.auth.setAuthProviderAsSessionCookie(AuthProvider.KINDE);
          // Drive the auth token cache through the same login
          // transition. Without this, authStateAtom stays at its
          // pre-login value and useIsAuthenticated()-gated consumers
          // (including AuthWrapper-gated routes) don't see the user
          // as authenticated until the next window-focus event.
          // clearAuthCache() drops any stale state; the follow-up
          // getAccessToken() reads through the cache's Kinde-desktop
          // bridge atom and transitions authStateAtom to
          // 'authenticated'.
          const { clearAuthCache, getAccessToken } = await import('@/lib/auth');
          clearAuthCache();
          await getAccessToken();
        }
      } else if (!isAuthenticated && storedToken) {
        // SDK finished loading, getToken() returned null, and Kinde reports
        // not authenticated — the persisted token is stale (e.g. Windows
        // restart where Kinde session is lost). Clear it so pages/index.tsx
        // can show the login page instead of an infinite loader.
        //
        // Skip if a logout is already in progress — the logout effect in
        // authModel handles its own cleanup. Calling clearPersistedModels()
        // here during logout cancels in-flight queries and removes cached
        // data while components are still mounted, causing 401 refetch errors.
        const isLogoutInProgress = isLogoutInFlight();
        if (isLogoutInProgress) {
          logger.info('[TauriWrapper] Skipping stale token cleanup — logout in progress');
          return;
        }
        logger.info('[TauriWrapper] No Kinde session — clearing stale persisted token', {
          isAuthenticated,
          isLoading,
        });
        void dispatch.auth.clearPersistedModels();
      }
    };
    void setup();
  }, [
    isAuthenticated,
    isLoading,
    auth0IsLoading,
    auth0IsAuthenticated,
    getToken,
    dispatch.auth,
    isAuth0Desktop,
    storedToken,
    authProvider,
  ]);
  const { userRoleList, isLoadingTeamDetails } = useCombinedQueriesTeamGeneralInfo();

  useEffect(() => {
    if (storedToken) {
      void queryClient.invalidateQueries({
        queryKey: [
          GET_TEAM_DETAILS,
          { teamId: joinedTeamsData?.joined_teams[0]?.team_id },
        ],
      });
      void refetchDocumentTemplates();
    }
  }, [joinedTeamsData?.joined_teams, storedToken, queryClient, refetchDocumentTemplates]);

  useEffect(() => {
    // Determine auth state based on provider
    // For Auth0 Desktop: use user from API and storedToken
    // For Kinde: use kindeUser and storedToken
    const isLoggedIn = isAuth0Desktop
      ? !!(user && storedToken)
      : !!(kindeUser && storedToken);

    if (!isLoggedIn) {
      return;
    }

    // Get user info from appropriate source
    // TODO Jonathan - the property will be still returned for auth0, we may need to change the property name after the migration
    const authId = isAuth0Desktop ? user?.kinde_user_id || '' : kindeUser?.id || '';
    const email = isAuth0Desktop ? user?.email || '' : kindeUser?.email || '';
    const givenName = isAuth0Desktop
      ? user?.first_name || ''
      : kindeUser?.given_name || '';
    const familyName = isAuth0Desktop
      ? user?.last_name || ''
      : kindeUser?.family_name || '';

    dispatch.analyticsScribe.identify({
      authID: authId,
      userTraits: {
        email,
        user_ref_id: user?.user_ref_id,
        App: getAppType(),
        country: user?.country || '',
        region: user?.country ? countriesToRegion[user?.country] : undefined,
        given_name: givenName,
        family_name: familyName,
        phone_number: user?.phone_number || '',
        user_team_id: teamDetails?.team.id,
        user_team_name: teamDetails?.team.name,
        user_team_role: teamDetails?.team.team_members
          ? teamDetails?.team.team_members.find(
              (member) => member.kinde_user_id === authId
            )?.user_role
          : undefined,
        user_team_plan: teamDetails?.team.team_members
          ? teamDetails?.team.team_members.find(
              (member) => member.kinde_user_id === authId
            )?.tier
          : undefined,
        user_joined_team_date: teamDetails?.team.team_members
          ? teamDetails?.team.team_members.find(
              (member) => member.kinde_user_id === authId
            )?.created_at
          : undefined,
        user_created_date: user?.created_at,
        user_tier: user?.tier,
        user_role_list: userRoleList,
        team_tier: teamDetails?.team.tier,
        effective_tier:
          billingInfo?.third_party_license?.latest_tier || billingInfo?.tier,
        onboarding_selected_ehr_provider: user?.ehr_provider ?? null,
        ehr_provider: userEhr?.ehr_profile ? userEhr.ehr_profile.ehr_provider : null,
        has_integration: !!userEhr?.ehr_profile,
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
        source: integrationOptions?.ehrName || 'Heidi',
        signup_attributed_product: user?.signup_source,
        integration_requests: user?.integration_requests as IntegrationRequest[],
        mfa_enabled: user?.user_mfa_info?.is_mfa_enabled,
        mfa_trigger_source: user?.user_mfa_info?.set_by,
        user_team_mfa_enabled: !!teamDetails?.team.mfa_info?.method,
        opted_in_experimental: user?.session_settings?.opted_in_experimental,
        session_page_v2_enrollment_source: getSessionPageV2EnrollmentSource(user),
        go_live_time_stamp: teamDetails?.team.go_live_time_stamp,
        onboarding_free_text: teamDetails?.team.onboarding_free_text,
        ...getSpecialtyIdentifyTraits(user?.specialty_id, specialties),
        other_specialty: user?.other_specialty,
        display_language: user?.language_time_settings?.display_language,
        seen_modals: user?.seen_modals,
        from: 'tauri-auth.tsx',
        cpuArch,
        evidence_access: user?.evidence_access ?? undefined,
        is_evidence_activated: user?.is_evidence_activated ?? undefined,
        is_managed: !!user?.is_managed,
      },
    });

    setDatadogProperties(authId, {
      userTier: user?.tier,
      teamTier: teamDetails?.team.tier,
      teamId: teamDetails?.team.id,
      teamName: teamDetails?.team.name ?? null,
      workspaceId: workspace?.workspace_id ?? null,
      ehrProvider: userEhr?.ehr_profile?.ehr_provider,
      hasIntegration: !!userEhr?.ehr_profile,
      source: integrationOptions?.ehrName || 'Heidi',
      userRefId: user?.user_ref_id,
      idpSub: storedToken ? extractIdpSubFromAccessToken(storedToken) : undefined,
    });
  }, [
    billingInfo?.tier,
    dispatch.analyticsScribe,
    integrationOptions,
    cpuArch,
    storedToken,
    kindeUser,
    teamDetails,
    workspace,
    workspaceId,
    user,
    userEhr?.ehr_profile,
    billingInfo?.third_party_license?.latest_tier,
    userRoleList,
    specialties,
    isAuth0Desktop,
  ]);

  useEffect(() => {
    if (teamDetails?.team) {
      setActiveTeam(teamDetails.team);
    }
  }, [teamDetails?.team]);

  useEffect(() => {
    if (user?.intercom_user_id) {
      void setIntercomProperties({
        user,
        teamDetails,
        userEhr,
        integrationOptions,
        billingInfo,
        workspace,
        locale: user?.language_time_settings?.display_language,
        isSessionPageV2Enabled,
        isProjectsEnabled,
        databaseRegion,
        hcpVerificationStatus,
      });
    }
  }, [
    user,
    isAuthenticated,
    teamDetails?.team.id,
    teamDetails,
    userEhr,
    integrationOptions,
    billingInfo,
    workspace,
    user?.language_time_settings?.display_language,
    isSessionPageV2Enabled,
    isProjectsEnabled,
    databaseRegion,
    hcpVerificationStatus,
  ]);

  useEffect(() => {
    // Skip this check for Auth0 Desktop - it has its own logout mechanism.
    // Note: authProvider is set early in launchDesktopAuthLogin() before the login flow starts,
    // so isAuth0Desktop will be true immediately when the user clicks any Auth0 login button.
    if (isAuth0Desktop) {
      return;
    }
    // Skip if Auth0 Desktop status is still loading (prevents race condition during Auth0 login)
    if (auth0IsLoading) {
      return;
    }
    // Skip if Auth0 Desktop reports authenticated - this handles the edge case where
    // the token is set but authProvider hasn't propagated through Redux yet
    if (auth0IsAuthenticated) {
      return;
    }
    // Leftover token but Kinde reports logged out — clear the stale persisted state.
    // Guard: only clear if the JWT is actually expired. A non-expired token means
    // a fresh login just set it but isAuthenticated hasn't caught up yet (race
    // between Redux state and Kinde SDK hook updates). This synchronous check
    // eliminates the race window entirely.
    if (!isAuthenticated && !isLoading && storedToken) {
      if (!isJWTExpired(storedToken)) {
        logger.info('[TauriWrapper] Skipping cleanup — token is not expired', {
          isAuthenticated,
          isLoading,
        });
        return;
      }
      logger.info('[TauriWrapper] Clearing persisted models — token is expired', {
        isAuthenticated,
        isLoading,
        hasStoredToken: !!storedToken,
        isAuth0Desktop,
        auth0IsLoading,
        auth0IsAuthenticated,
      });
      void dispatch.auth.clearPersistedModels();
    }
  }, [
    dispatch.auth,
    isAuthenticated,
    isLoading,
    storedToken,
    isAuth0Desktop,
    auth0IsLoading,
    auth0IsAuthenticated,
  ]);

  const setHasUpdatedAbility = useSetAtom(hasUpdatedAbilityAtom);

  // update ability
  useEffect(() => {
    // wait until both queries are fetched
    if (!isJoinedTeamsFetched) {
      return;
    }

    if (isLoadingTeamDetails) {
      return;
    }

    // if user role list, set corresponding permissions
    if (userRoleList) {
      const uniquePermissions = Array.from(
        new Map(
          userRoleList
            .flatMap((role) => role.permissions)
            .map((permission) => [permission.code, permission])
        ).values()
      );

      updateAbility({ permissions: uniquePermissions });
      setHasUpdatedAbility(true);
    } else {
      accessAllPermissions();
    }
  }, [userRoleList, isJoinedTeamsFetched, isLoadingTeamDetails, setHasUpdatedAbility]);

  useOverlaySetEnabled({ authenticated: isAuthenticated && !isLoading });

  // Wait for Auth0 Desktop status to load before rendering children
  // This prevents race conditions where the token is set but authProvider hasn't propagated yet
  if (auth0IsLoading) {
    return null;
  }

  return <CurrentUserProvider userQuery={userQuery}>{children}</CurrentUserProvider>;
};

export default TauriAuth;
